import type { ReactElement } from "react"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import type { PriceVariant } from "@onestore/hel/dist/components/molecules/PriceMol/priceMol.types"
import { PriceVariantType } from "@onestore/hel/dist/components/molecules/PriceMol/priceMol.types"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import WidthOrg from "@onestore/hel/dist/components/organisms/WidthOrg"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import type { PeriodInfo } from "@core/period-info"
import type { PriceType } from "@core/types"
import DetailedPrice from "@gatsby-plugin-generic-page/components/DetailedPrice"
import MarkdownTile from "@gatsby-plugin-generic-page/components/Markdown/Tile"
import LowestPriceInfo from "~/components/LowestPriceInfo"
import Tooltip from "~/components/Tooltip"
import type { PriceInfo } from "~/fragments/priceInfo"
import usePriceConfigurationQuery from "~/hooks/usePriceConfigurationQuery"

type SearchResultPriceProps = {
  periodInfo?: PeriodInfo | null
  priceType?: PriceType
  periodText?: string
  tooltip?: string
  overridePrice?: string
  isMainResult?: boolean
  priceInfo?: PriceInfo
  priceVariant?: PriceVariant
}

export default function SearchResultPrice({
  periodInfo,
  priceType,
  periodText,
  tooltip,
  overridePrice,
  isMainResult,
  priceInfo,
  priceVariant,
}: SearchResultPriceProps): ReactElement<SearchResultPriceProps> {
  const hasPromotion = periodInfo?.hasPromotion() ?? false
  const isSmallOnly = useBreakpoint("small", "medium")

  const periodInfoValues = periodInfo
    ? periodInfo.getPrices(priceType)
    : {
        priceValue: "",
        lowestPrice: "",
        lowestPricePercent: "",
        renewPrice: "",
      }

  const { priceValue, lowestPrice } = periodInfoValues
  const { showDetailedPrices } = usePriceConfigurationQuery()

  return showDetailedPrices && periodInfo ? (
    <WidthOrg widthSpace={38}>
      <DetailedPrice
        periodInfo={periodInfo}
        priceInfo={priceInfo}
        variant={
          priceVariant || {
            type: PriceVariantType.MEDIUM,
            align: isSmallOnly ? "left" : "right",
          }
        }
        hasNoTopLine
      />
    </WidthOrg>
  ) : (
    <>
      <FlexContainerOrg alignItems="center" justifyContent="flex-end">
        <TextAtm
          typography="medium3_s"
          color={hasPromotion ? "primary" : undefined}
        >
          {overridePrice || priceValue}{" "}
          <TextAtm typography="nano1" emphasis="high">
            {periodText}
          </TextAtm>
        </TextAtm>

        {tooltip && hasPromotion ? (
          <PushOrg leftSpace={0.5}>
            <Tooltip iconSize="large">
              <AlignContentOrg horizontalAlign="left">
                <MarkdownTile>{tooltip}</MarkdownTile>
              </AlignContentOrg>
            </Tooltip>
          </PushOrg>
        ) : null}
      </FlexContainerOrg>

      {hasPromotion ? (
        <PushOrg
          topSpace={
            isMainResult
              ? 2
              : {
                  large: 1,
                  medium: 1,
                  small: 2,
                }
          }
        >
          <LowestPriceInfo value={lowestPrice} />
        </PushOrg>
      ) : undefined}
    </>
  )
}
